import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { addDays, isBefore, startOfDay, format, parseISO } from "date-fns";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { getPropertyGroups, get as getProperties } from "../../property/api/actions";
import { accountQuickSearch } from "../../accountsSearch/api/actions";

function DisconnectHoldForm(props) {
  const [formData, setFormData] = useState({
    holdType: 'GROUP',
    groupId: '',
    propertyId: '',
    accountId: '',
    accountNumber: '',
    reason: '',
    notes: '',
    expiresAt: moment(addDays(new Date(), 30)),
    isActive: 1,
    liftReason: '',
  });

  const [errors, setErrors] = useState({});
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const [isSearchingAccounts, setIsSearchingAccounts] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  useEffect(() => {
    if (props.open) {
      console.log('Form opened with props:', { 
        liftingHold: props.liftingHold, 
        editingHold: props.editingHold,
        open: props.open 
      });
      // Fetch all property groups for dropdown (use high limit to get all groups)
      props.getPropertyGroups({ limit: 10000, page: 1 });
      props.getProperties();
      
      if (props.editingHold) {
        setFormData({
          holdType: props.editingHold.holdType || 'GROUP',
          groupId: props.editingHold.groupId || '',
          propertyId: props.editingHold.propertyId || '',
          accountId: props.editingHold.accountId || '',
          accountNumber: props.editingHold.accountNumber || '',
          reason: props.editingHold.reason || '',
          notes: props.editingHold.notes || '',
          expiresAt: props.editingHold.expiresAt 
            ? moment(parseISO(props.editingHold.expiresAt)) 
            : moment(addDays(new Date(), 30)),
          isActive: props.editingHold.isActive !== undefined ? props.editingHold.isActive : 1,
          liftReason: props.liftingHold ? '' : (props.editingHold.liftReason || ''),
        });
        // Set selectedAccount for editing mode if it's an account hold
        if (props.editingHold.holdType === 'ACCOUNT' && props.editingHold.accountId) {
          setSelectedAccount({
            id: props.editingHold.accountId,
            accountNum: props.editingHold.accountNumber,
            firstName: '',
            lastName: ''
          });
        }
      } else {
        setFormData({
          holdType: 'GROUP',
          groupId: '',
          propertyId: '',
          accountId: '',
          accountNumber: '',
          reason: '',
          notes: '',
          expiresAt: moment(addDays(new Date(), 30)),
          isActive: 1,
          liftReason: '',
        });
        setSelectedAccount(null);
      }
      setErrors({});
    }
  }, [props.open, props.editingHold]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear selected account if hold type changes away from ACCOUNT
    if (field === 'holdType' && value !== 'ACCOUNT') {
      setSelectedAccount(null);
    }
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const handleAccountSearch = async (query) => {
    if (!query || query.length < 2) {
      return;
    }
    
    setIsSearchingAccounts(true);
    try {
      await props.accountQuickSearch({ searchQuery: query });
    } catch (error) {
      console.error('Error searching accounts:', error);
    } finally {
      setIsSearchingAccounts(false);
    }
  };

  const handleAccountSelect = (account) => {
    if (account) {
      setSelectedAccount(account);
      setFormData(prev => ({
        ...prev,
        accountId: account.id,
        accountNumber: account.accountNum || account.accountNumber || account.account
      }));
    } else {
      setSelectedAccount(null);
      setFormData(prev => ({
        ...prev,
        accountId: '',
        accountNumber: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (props.liftingHold) {
      if (!formData.liftReason || !formData.liftReason.trim()) {
        newErrors.liftReason = "Please enter a reason for lifting this hold";
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }
    
    if (!formData.holdType) {
      newErrors.holdType = "Please select a hold type";
    }
    
    if (formData.holdType === 'GROUP' && !formData.groupId) {
      newErrors.groupId = "Please select a property group";
    }
    
    if (formData.holdType === 'PROPERTY' && !formData.propertyId) {
      newErrors.propertyId = "Please select a property";
    }
    
    if (formData.holdType === 'ACCOUNT' && !props.editingHold && !formData.accountId) {
      newErrors.accountId = "Please select an account";
    }
    
    if (!formData.reason || !formData.reason.trim()) {
      newErrors.reason = "Please enter a reason for the hold";
    }
    
    const expiresDate = formData.expiresAt ? (moment.isMoment(formData.expiresAt) ? formData.expiresAt.toDate() : formData.expiresAt) : null;
    if (!expiresDate || isBefore(expiresDate, startOfDay(new Date()))) {
      newErrors.expiresAt = "Please select a future expiration date";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      holdType: 'GROUP',
      groupId: '',
      propertyId: '',
      accountId: '',
      accountNumber: '',
      reason: '',
      notes: '',
      expiresAt: moment(addDays(new Date(), 30)),
      isActive: 1,
      liftReason: '',
    });
    setSelectedAccount(null);
    setAccountSearchQuery('');
    setErrors({});
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    if (props.liftingHold) {
      try {
        await props.onLift(props.editingHold, formData.liftReason.trim());
      } catch (error) {
        console.log('Error lifting disconnect hold:', error);
        // TODO: Show error snackbar
      }
      return;
    }

    const expiresDate = moment.isMoment(formData.expiresAt) ? formData.expiresAt.toDate() : formData.expiresAt;
    const holdData = {
      holdType: formData.holdType,
      reason: formData.reason.trim(),
      notes: formData.notes.trim(),
      expiresAt: format(startOfDay(expiresDate), "yyyy-MM-dd HH:mm:ss"),
      isActive: formData.isActive,
      liftReason: formData.liftReason.trim(),
    };

    // Add the appropriate ID based on hold type
    if (formData.holdType === 'GROUP' && formData.groupId) {
      holdData.groupId = parseInt(formData.groupId);
    }
    
    if (formData.holdType === 'PROPERTY' && formData.propertyId) {
      holdData.propertyId = parseInt(formData.propertyId);
    }
    
    if (formData.holdType === 'ACCOUNT' && formData.accountId) {
      holdData.accountId = formData.accountId;
    }

    try {
      if (props.editingHold) {
        await props.onSave(holdData, true, props.editingHold.uuid); // true for update, pass UUID separately
      } else {
        await props.onSave(holdData, false); // false for create
        // Reset form after successful creation
        resetForm();
      }
    } catch (error) {
      console.error('Error saving disconnect hold:', error);
      // TODO: Show error snackbar
    }
  };

  const propertyGroups = props.properties?.propertyGroups?.items || [];
  const properties = props.properties?.data?.propertiesList?.items || [];
  const disconnectHolds = props.disconnectHolds?.data?.disconnectHoldsList?.items || [];

  // Filter out groups that already have active holds
  const getAvailableGroups = () => {
    if (props.editingHold) {
      // When editing, include the current group
      return propertyGroups;
    }
    
    // Get group IDs that have active holds
    const groupsWithActiveHolds = disconnectHolds
      .filter(hold => hold.holdType === 'GROUP' && hold.isActive)
      .map(hold => hold.groupId);
    
    // Filter out groups that have active holds
    return propertyGroups.filter(group => !groupsWithActiveHolds.includes(group.id));
  };

  const availableGroups = getAvailableGroups();

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Dialog 
        open={props.open} 
        onClose={props.onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {props.liftingHold ? 'Lift Disconnect Hold' : props.editingHold ? 'Edit Disconnect Hold' : 'Create New Disconnect Hold'}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {!props.liftingHold && (
              <>
                <FormControl fullWidth error={!!errors.holdType}>
                  <InputLabel>Hold Type</InputLabel>
                  <Select
                    value={formData.holdType}
                    onChange={(e) => handleInputChange('holdType', e.target.value)}
                    label="Hold Type"
                    disabled={!!props.editingHold}
                  >
                    <MenuItem value="GROUP">Group</MenuItem>
                    <MenuItem value="PROPERTY">Property</MenuItem>
                    <MenuItem value="ACCOUNT">Account</MenuItem>
                  </Select>
                  {errors.holdType && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.holdType}
                    </Typography>
                  )}
                </FormControl>

                {props.editingHold ? (
              <TextField
                fullWidth
                label="Target"
                value={
                  props.editingHold.holdType === 'GROUP' 
                    ? props.editingHold.groupName || 'Unknown Group'
                    : props.editingHold.holdType === 'PROPERTY'
                    ? props.editingHold.propertyName || 'Unknown Property'
                    : props.editingHold.holdType === 'ACCOUNT'
                    ? props.editingHold.accountNum || 'Unknown Account'
                    : 'Unknown Target'
                }
                disabled
              />
            ) : (
              <>
                {formData.holdType === 'GROUP' && (
                  <FormControl fullWidth error={!!errors.groupId}>
                    <InputLabel>Property Group</InputLabel>
                    <Select
                      value={formData.groupId}
                      onChange={(e) => handleInputChange('groupId', e.target.value)}
                      label="Property Group"
                      disabled={availableGroups.length === 0}
                    >
                      {availableGroups.length > 0 ? (
                        availableGroups.map((group) => (
                          <MenuItem key={group.id} value={group.id}>
                            {group.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>
                          No available groups (all groups have active holds)
                        </MenuItem>
                      )}
                    </Select>
                    {errors.groupId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {errors.groupId}
                      </Typography>
                    )}
                    {availableGroups.length === 0 && !props.editingHold && (
                      <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
                        All property groups currently have active holds. You cannot create a new group hold at this time.
                      </Typography>
                    )}
                  </FormControl>
                )}

                {formData.holdType === 'PROPERTY' && (
                  <FormControl fullWidth error={!!errors.propertyId}>
                    <InputLabel>Property</InputLabel>
                    <Select
                      value={formData.propertyId}
                      onChange={(e) => handleInputChange('propertyId', e.target.value)}
                      label="Property"
                    >
                      {properties.map((property) => (
                        <MenuItem key={property.id} value={property.id}>
                          {property.name} - {property.address}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.propertyId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {errors.propertyId}
                      </Typography>
                    )}
                  </FormControl>
                )}

                {formData.holdType === 'ACCOUNT' && (
                  <Autocomplete
                    fullWidth
                    options={props.accountsSearch?.quickSearchResult || []}
                    getOptionLabel={(option) => 
                      option ? `${option.accountNum || option.accountNumber || option.account} - ${option.firstName || option.customerName || option.name || 'Unknown Customer'} ${option.lastName || ''}`.trim() : ''
                    }
                    value={selectedAccount}
                    onChange={(event, newValue) => handleAccountSelect(newValue)}
                    onInputChange={(event, newInputValue) => {
                      setAccountSearchQuery(newInputValue);
                      if (newInputValue.length >= 2) {
                        handleAccountSearch(newInputValue);
                      }
                    }}
                    loading={isSearchingAccounts}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Account"
                        error={!!errors.accountId}
                        helperText={errors.accountId || "Search for an account by number or customer name"}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isSearchingAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {option.accountNum || option.accountNumber || option.account}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.firstName || ''} {option.lastName || ''} {option.customerName || option.name || 'Unknown Customer'}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    noOptionsText="No accounts found"
                    loadingText="Searching accounts..."
                  />
                )}
              </>
            )}

            <TextField
              fullWidth
              label="Reason"
              value={formData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              error={!!errors.reason}
              helperText={errors.reason || "Required: Explain why this hold is being placed"}
              multiline
              rows={3}
            />

            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              helperText="Optional: Additional details about this hold"
              multiline
              rows={2}
            />

            <DatePicker
              label="Expiration Date"
              value={formData.expiresAt}
              onChange={(date) => handleInputChange('expiresAt', date)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.expiresAt,
                  helperText: errors.expiresAt || "When should this hold automatically expire?"
                }
              }}
            />

              </>
            )}

            {props.liftingHold && (
              <>
                <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Hold Type:</strong> {props.editingHold?.holdType || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Target:</strong> {
                      props.editingHold?.holdType === 'GROUP' 
                        ? props.editingHold.groupName || 'Unknown Group'
                        : props.editingHold?.holdType === 'PROPERTY'
                        ? props.editingHold.propertyName || 'Unknown Property'
                        : props.editingHold?.holdType === 'ACCOUNT'
                        ? props.editingHold.accountNum || 'Unknown Account'
                        : 'Unknown Target'
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Reason:</strong> {props.editingHold?.reason || 'No reason provided'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Expires:</strong> {props.editingHold?.expiresAt ? new Date(props.editingHold.expiresAt).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="Lift Reason"
                  value={formData.liftReason}
                  onChange={(e) => handleInputChange('liftReason', e.target.value)}
                  helperText="Required"
                  multiline
                  rows={3}
                  error={!formData.liftReason.trim()}
                />
              </>
            )}

            {!props.liftingHold && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Hold Types:</strong><br/>
                  • <strong>Group:</strong> Prevents disconnection for all properties in a group<br/>
                  • <strong>Property:</strong> Prevents disconnection for a specific property<br/>
                  • <strong>Account:</strong> Prevents disconnection for specific accounts
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={props.onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            disabled={props.isLoading}
          >
            {props.liftingHold ? 'Lift Hold' : props.editingHold ? 'Update Hold' : 'Create Hold'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

DisconnectHoldForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onLift: PropTypes.func,
  editingHold: PropTypes.object,
  liftingHold: PropTypes.bool,
  isLoading: PropTypes.bool,
  getPropertyGroups: PropTypes.func.isRequired,
  getProperties: PropTypes.func.isRequired,
  accountQuickSearch: PropTypes.func.isRequired,
  properties: PropTypes.object.isRequired,
  disconnectHolds: PropTypes.object.isRequired,
  accountsSearch: PropTypes.object.isRequired,
};

function State(state) {
  return {
    properties: state.properties,
    disconnectHolds: state.disconnectHolds,
    accountsSearch: state.accountsSearch,
  };
}

export default connect(State, { getPropertyGroups, getProperties, accountQuickSearch })(DisconnectHoldForm);
